import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';

const DEFAULT_PROJECT_BOUNDS = Object.freeze({
  minX: -2000,
  minZ: -2000,
  maxX: 2000,
  maxZ: 2000
});

function normalizeProjectBounds(bounds) {
  const source = bounds?.bounds && typeof bounds.bounds === 'object' ? bounds.bounds : bounds;
  const minX = Number.isFinite(Number(source?.minX)) ? Number(source.minX) : null;
  const minZ = Number.isFinite(Number(source?.minZ)) ? Number(source.minZ) : null;
  const maxX = Number.isFinite(Number(source?.maxX)) ? Number(source.maxX) : null;
  const maxZ = Number.isFinite(Number(source?.maxZ)) ? Number(source.maxZ) : null;
  if (minX === null || minZ === null || maxX === null || maxZ === null) return null;

  const normalized = {
    minX: Math.min(minX, maxX),
    minZ: Math.min(minZ, maxZ),
    maxX: Math.max(minX, maxX),
    maxZ: Math.max(minZ, maxZ)
  };

  if (Number.isFinite(Number(source?.minY))) normalized.minY = Number(source.minY);
  if (Number.isFinite(Number(source?.maxY))) normalized.maxY = Number(source.maxY);
  return normalized;
}

function projectObjectBounds(instance) {
  const explicit = normalizeProjectBounds(instance?.bounds);
  if (explicit) return explicit;

  const x = Number.isFinite(Number(instance?.x)) ? Number(instance.x) : null;
  const z = Number.isFinite(Number(instance?.z)) ? Number(instance.z) : null;
  if (x === null || z === null) return null;

  const width = Math.max(1, Number.isFinite(Number(instance?.width)) ? Number(instance.width) : 1);
  const depth = Math.max(1, Number.isFinite(Number(instance?.depth)) ? Number(instance.depth) : 1);
  const bounds = {
    minX: x - width / 2,
    minZ: z - depth / 2,
    maxX: x + width / 2,
    maxZ: z + depth / 2
  };
  if (Number.isFinite(Number(instance?.height))) bounds.maxY = Math.max(1, Number(instance.height));
  return bounds;
}

function combineProjectBounds(boundsList = [], options = {}) {
  const boxes = (boundsList || []).map(normalizeProjectBounds).filter(Boolean);
  if (!boxes.length) {
    return options.defaultBounds === null
      ? null
      : { ...(options.defaultBounds || DEFAULT_PROJECT_BOUNDS) };
  }

  const result = {
    minX: Math.min(...boxes.map(bounds => bounds.minX)),
    minZ: Math.min(...boxes.map(bounds => bounds.minZ)),
    maxX: Math.max(...boxes.map(bounds => bounds.maxX)),
    maxZ: Math.max(...boxes.map(bounds => bounds.maxZ))
  };
  const maxYValues = boxes.map(bounds => bounds.maxY).filter(value => Number.isFinite(Number(value)));
  if (maxYValues.length) result.maxY = Math.max(...maxYValues);
  const minYValues = boxes.map(bounds => bounds.minY).filter(value => Number.isFinite(Number(value)));
  if (minYValues.length) result.minY = Math.min(...minYValues);
  return result;
}

function boundsFromProjectObjects(objects = [], options = {}) {
  return combineProjectBounds((objects || []).map(projectObjectBounds).filter(Boolean), options);
}

function collectRegisteredProjectBounds() {
  const bounds = typeof SystemRegistry.collectProjectBounds === 'function'
    ? SystemRegistry.collectProjectBounds()
    : [];
  return (bounds || []).map(normalizeProjectBounds).filter(Boolean);
}

function computeProjectBounds(options = {}) {
  let bounds = collectRegisteredProjectBounds();
  if (!bounds.length) {
    const objects = typeof SystemRegistry.collectProjectObjects === 'function'
      ? SystemRegistry.collectProjectObjects()
      : [];
    bounds = (objects || []).map(projectObjectBounds).filter(Boolean);
  }
  return combineProjectBounds(bounds, options);
}

export {
  DEFAULT_PROJECT_BOUNDS,
  normalizeProjectBounds,
  projectObjectBounds,
  combineProjectBounds,
  boundsFromProjectObjects,
  collectRegisteredProjectBounds,
  computeProjectBounds
};
